/**
 * Hand-written types mirroring the backend DTOs/views (SPEC §4, treated as
 * fixed). All money fields are INTEGER KOPEKS; `date` fields are `YYYY-MM-DD`
 * business dates; `createdAt`/`updatedAt`/`deletedAt` are ISO timestamps.
 */

export type OperationType = 'PURCHASE' | 'ISSUE' | 'STORE_PAYMENT' | 'SKLAD_PAYMENT' | 'EXPENSE';

/** How a STORE_PAYMENT arrived. Null on rows created before the field existed. */
export type PaymentMethod = 'CASH' | 'TERMINAL';

export const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'TERMINAL'];

/** STORE_PAYMENT sums split by method; the three add up to the paid total. */
export interface PaidByMethod {
  cash: number;
  terminal: number;
  unspecified: number;
}

export const OPERATION_TYPES: OperationType[] = [
  'PURCHASE',
  'ISSUE',
  'STORE_PAYMENT',
  'SKLAD_PAYMENT',
  'EXPENSE',
];

// ---------------------------------------------------------------- auth

/** PLATFORM_ADMIN is the cross-tenant operator (creates businesses, may «enter»
 * any one of them); it never appears in a business's own user list. */
export type Role = 'PLATFORM_ADMIN' | 'SUPERADMIN' | 'USER' | 'SELLER';

/** The business the current user works inside. */
export interface SafeBusiness {
  id: string;
  name: string;
  /** The ONE business that owns the MoySklad account — the only one with the
   * МойСклад settings tab and synced накладные. */
  moyskladEnabled: boolean;
}

export interface SafeUser {
  id: string;
  login: string;
  name: string;
  /** EFFECTIVE role: a PLATFORM_ADMIN who has selected a business reads as its
   * SUPERADMIN (`platformAdmin` stays true). */
  role: Role;
  isActive: boolean;
  /** The store a SELLER is bound to; null for SUPERADMIN/USER. */
  storeId: string | null;
  /** Restricted USER's allowed-stores subset; null = sees every store. */
  storeIds: string[] | null;
  /** The business in scope; null only for a PLATFORM_ADMIN with none selected. */
  businessId: string | null;
  platformAdmin: boolean;
  business: SafeBusiness | null;
}

// ---------------------------------------------------------------- businesses (platform admin)

export interface Business {
  id: string;
  name: string;
  isArchived: boolean;
  moyskladEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  counts: { users: number; stores: number };
}

export interface CreateBusinessBody {
  name: string;
  adminLogin: string;
  adminName: string;
  adminPassword: string;
}

export interface UpdateBusinessBody {
  name?: string;
  isArchived?: boolean;
  moyskladEnabled?: boolean;
}

export interface CreateBusinessAdminBody {
  login: string;
  name: string;
  password: string;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  user: SafeUser;
}

// ---------------------------------------------------------------- users (superadmin)

export interface ManagedUser {
  id: string;
  login: string;
  name: string;
  role: Role;
  isActive: boolean;
  storeId: string | null;
  /** USER's allowed-stores subset; [] = sees every store. */
  storeIds: string[];
  createdAt: string;
}

export interface CreateUserBody {
  login: string;
  name: string;
  password: string;
  role?: Role;
  storeId?: string;
  storeIds?: string[];
}

export interface UpdateUserBody {
  name?: string;
  role?: Role;
  isActive?: boolean;
  storeId?: string;
  storeIds?: string[];
}

// ------------------------------------------------- operation requests (seller)

export type RequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** A seller's proposed money entry awaiting admin approval. Never a ledger row
 * until APPROVED (which materializes a real Operation). */
export interface OperationRequest {
  id: string;
  type: OperationType; // only STORE_PAYMENT | EXPENSE in practice
  date: string;
  amount: number;
  storeId: string;
  storeName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  paymentMethod: PaymentMethod | null;
  deductFromStoreBalance: boolean;
  comment: string | null;
  status: RequestStatus;
  createdById: string | null;
  createdByName: string;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  operationId: string | null;
  createdAt: string;
  /** Admin queue only: shared by every look-alike PENDING row in the response. */
  duplicateGroupId: string | null;
  /** Admin queue only: a ledger Operation already carries this exact money. */
  duplicateOperationId: string | null;
  /** The seller answered «да, это другая оплата» at submit time. */
  duplicateConfirmedAt: string | null;
}

export interface CreateOperationRequestBody {
  type: OperationType;
  date: string;
  amount: number;
  categoryId?: string;
  paymentMethod?: PaymentMethod;
  /** EXPENSE-only: subtract from the seller's store balance on approval. */
  deductFromStoreBalance?: boolean;
  comment?: string;
  /** Re-send after the look-alike warning: yes, this really is another payment. */
  confirmDuplicate?: boolean;
}

export interface UpdateOperationRequestBody {
  date?: string;
  amount?: number;
  categoryId?: string;
  paymentMethod?: PaymentMethod;
  comment?: string;
  /** EXPENSE «вычесть из остатка магазина» — part of the duplicate identity. */
  deductFromStoreBalance?: boolean;
}

export type ApproveBlockReason = 'DUPLICATE_OPERATION' | 'DUPLICATE_IN_BATCH';

/** A request the batch refused to approve because it looks like a copy. It is
 * still PENDING — approving it needs an explicit per-id confirmation. */
export interface BlockedApproval {
  requestId: string;
  reason: ApproveBlockReason;
  operationId: string | null;
  twinRequestId: string | null;
  storeId: string;
  storeName: string | null;
  date: string;
  amount: number;
}

/** Result of the bulk «Утвердить все» call. */
export interface ApproveBatchResult {
  approved: number;
  /** Ids that were unknown or no longer PENDING when the batch ran. */
  skipped: number;
  /** Look-alikes left pending for the admin to judge one by one. */
  blocked: BlockedApproval[];
}

/** Body of the 409 the server returns when a submit looks like a repeat. */
export interface DuplicateSuspectedPayload {
  code: 'DUPLICATE_SUSPECTED';
  duplicates: OperationRequest[];
}

/** PENDING count per store, for the admin approval badge. */
export interface PendingApprovalCount {
  storeId: string;
  storeName: string;
  count: number;
}

// ---------------------------------------------------------------- directories

export interface Store {
  id: string;
  name: string;
  openingBalance: number;
  isArchived: boolean;
  sortOrder: number;
  /** MoySklad counterparty this store maps to (накладные), null = unmapped. */
  msCounterpartyId: string | null;
  /** Informational rates, basis points (250 = 2.5%); zeroed for sellers. */
  terminalFeeBps: number;
  taxBps: number;
  createdAt: string;
  updatedAt: string;
  /** Computed: openingBalance + SUM(ISSUE) − SUM(STORE_PAYMENT). */
  balance: number;
}

export interface Sklad {
  id: string;
  name: string;
  openingDebt: number;
  isArchived: boolean;
  sortOrder: number;
  /** Optional accent hex («#rrggbb»); tints this sklad's column in the store grid. */
  color?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Computed: openingDebt + SUM(PURCHASE) − SUM(SKLAD_PAYMENT). */
  debt: number;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  isArchived: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertStoreBody {
  name?: string;
  openingBalance?: number;
  sortOrder?: number;
  isArchived?: boolean;
  msCounterpartyId?: string | null;
  terminalFeeBps?: number;
  taxBps?: number;
}

export interface UpsertSkladBody {
  name?: string;
  openingDebt?: number;
  sortOrder?: number;
  color?: string | null;
  isArchived?: boolean;
}

export interface UpsertCategoryBody {
  name?: string;
  sortOrder?: number;
  isArchived?: boolean;
}

// ---------------------------------------------------------------- operations

export interface EntityRef {
  id: string;
  name: string;
}

export interface Operation {
  id: string;
  type: OperationType;
  date: string;
  amount: number;
  storeId: string | null;
  skladId: string | null;
  categoryId: string | null;
  pairId: string | null;
  /** Non-null → posted from a накладная; the row is document-owned and cannot
   * be edited/deleted directly (change it via the накладная instead). */
  shipmentId: string | null;
  paymentMethod: PaymentMethod | null;
  /** EXPENSE «вычесть из остатка магазина» flag (false for every other type). */
  deductFromStoreBalance: boolean;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  store?: EntityRef | null;
  sklad?: EntityRef | null;
  category?: EntityRef | null;
  /** Who entered / approved the row — present on list read models (journal,
   * ledger); null for rows older than the audit trail or written by the system. */
  enteredByName?: string | null;
  approvedByName?: string | null;
}

export interface CreateOperationBody {
  type: OperationType;
  date: string;
  amount: number;
  storeId?: string;
  skladId?: string;
  categoryId?: string;
  /** Required by the backend for STORE_PAYMENT. */
  paymentMethod?: PaymentMethod;
  /** EXPENSE-only: subtract this расход from its store's balance (needs storeId). */
  deductFromStoreBalance?: boolean;
  comment?: string;
}

export interface UpdateOperationBody {
  date?: string;
  amount?: number;
  storeId?: string | null;
  skladId?: string | null;
  categoryId?: string | null;
  paymentMethod?: PaymentMethod | null;
  deductFromStoreBalance?: boolean;
  comment?: string | null;
}

export interface PurchaseIssueBody {
  date: string;
  skladId: string;
  purchaseAmount: number;
  storeId: string;
  issueAmount: number;
  comment?: string;
}

export interface PurchaseIssueResult {
  purchase: Operation;
  issue: Operation;
  skladDebt: number;
  storeBalance: number;
}

export interface ListOperationsParams {
  type?: OperationType;
  storeId?: string;
  storeIds?: string[];
  skladId?: string;
  categoryId?: string;
  from?: string;
  to?: string;
  includeDeleted?: boolean;
  page?: number;
  pageSize?: number;
}

export interface OperationListResult {
  items: Operation[];
  total: number;
  page: number;
  pageSize: number;
}

/** Actions across the whole app (SPEC §1 — entity-generic audit). */
export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'RESTORE'
  | 'POST'
  | 'UNPOST'
  | 'TRANSFER'
  | 'RETURN'
  | 'UNDO_MOVEMENT'
  | 'PRICE_CHANGE'
  | 'PASSWORD_CHANGE'
  | 'PASSWORD_RESET'
  | 'ACTIVATE'
  | 'DEACTIVATE';

export type AuditEntity =
  | 'OPERATION'
  | 'OPERATION_REQUEST'
  | 'BUSINESS'
  | 'SHIPMENT'
  | 'SHIPMENT_ITEM'
  | 'SHIPMENT_MOVEMENT'
  | 'STORE'
  | 'SKLAD'
  | 'EXPENSE_CATEGORY'
  | 'MOYSKLAD_SETTINGS'
  | 'USER';

export interface AuditLogEntry {
  id: string;
  action: AuditAction;
  userName: string | null;
  snapshot: unknown;
  createdAt: string;
}

// ---------------------------------------------------------------- audit feed (superadmin)

export interface AuditFeedEntry {
  id: string;
  entityType: AuditEntity;
  entityId: string;
  action: AuditAction;
  userId: string | null;
  userName: string | null;
  summary: string | null;
  createdAt: string;
}

export interface AuditListParams {
  userId?: string;
  entityType?: AuditEntity;
  action?: AuditAction;
  entityId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditListResult {
  items: AuditFeedEntry[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------- reports

export interface MonthlyStoreRow {
  storeId: string;
  name: string;
  issued: number;
  paid: number;
  monthDelta: number;
  runningBalance: number;
}

export interface MonthlyReport {
  month: string;
  stores: MonthlyStoreRow[];
  totals: { issued: number; paid: number; monthDelta: number; runningBalance: number };
  paidByMethod: PaidByMethod;
  /** Informational: terminal fee + НДС over the month's terminal payments. */
  terminalCharges: { fee: number; tax: number };
  profit: { issued: number; purchases: number; expenses: number; profit: number };
  expensesByCategory: { categoryId: string; name: string; amount: number }[];
}

export interface LedgerRow {
  operation: Operation;
  runningBalance: number;
}

export interface LedgerReport {
  month: string;
  openingBalance: number;
  rows: LedgerRow[];
  closingBalance: number;
}

export interface SkladSummaryRow {
  skladId: string;
  name: string;
  debt: number;
}

// ---------------------------------------------------------------- moysklad / накладные

export type ShipmentStatus = 'PENDING_PRICING' | 'POSTED';
export type ShipmentSource = 'MOYSKLAD' | 'MANUAL';

export type ShipmentItemOrigin = 'MOYSKLAD' | 'MANUAL' | 'MOVED_IN';
export type MovementKind = 'TRANSFER' | 'RETURN';

/** A quantity that left this line (transfer/return) — for badges + undo. */
export interface ShipmentItemMovement {
  id: string;
  kind: MovementKind;
  quantityMilli: number;
  toStoreId: string | null;
  toStoreName: string | null;
  toShipmentId: string | null;
  /** RETURN written into a MoySklad «Возврат покупателя» — its id/number. */
  msReturnId: string | null;
  msReturnName: string | null;
}

/** The previous накладная's price for the same product at this store. */
export interface PrevPrice {
  priceKopeks: number;
  shipmentId: string;
  msName: string;
  msDate: string;
}

/** MoySklad return-draft state of a накладная (id null = creation failed). */
export interface MsReturnView {
  id: string | null;
  name: string | null;
  href: string | null;
  syncError: string | null;
  syncedAt: string | null;
}

export type ShipmentWarning = 'MS_RETURN_ALREADY_POSTED' | 'MS_RETURN_SYNC_FAILED';

export interface ShipmentItem {
  id: string;
  /** MOYSKLAD (re-sync-managed), MANUAL (owner-added), or MOVED_IN (transfer). */
  origin: ShipmentItemOrigin;
  /** The sklad this item came from; null for MoySklad rows (source sklad). */
  skladId: string | null;
  skladName: string | null;
  /** The sklad the line actually posts under (MoySklad source sklad for synced
   * lines, else its own sklad). Scopes a multi-sklad накладная's per-leg
   * drill-down to only that leg's lines. */
  legSkladId: string | null;
  name: string;
  /** Original quantity × 1000 (fractional kg without floats). */
  quantityMilli: number;
  /** quantityMilli − Σ active out-movements — what still counts for this store. */
  activeQuantityMilli: number;
  /** Σ active out-movements (transferred/returned away). */
  movedOutMilli: number;
  /** Active out-movements of this line. */
  movements: ShipmentItemMovement[];
  /** On a MOVED_IN mirror line: the накладная it was transferred in from. */
  movedInFromShipmentId: string | null;
  movedInFromStoreName: string | null;
  /** Purchase price/sum per line, kopeks (MoySklad line / owner-entered). */
  buyPriceKopeks: number | null;
  buySumKopeks: number | null;
  /** Owner's issue price per unit, kopeks; null = not priced yet. */
  issuePriceKopeks: number | null;
  /** Buy price on this store's previous накладная from the same sklad (null for sellers). */
  prevBuy: PrevPrice | null;
  /** Owner's issue price on this store's previous накладная for this product. */
  prevIssue: PrevPrice | null;
}

export interface ShipmentListItem {
  id: string;
  source: ShipmentSource;
  msName: string;
  msDate: string;
  storeId: string;
  storeName: string;
  skladId: string | null;
  skladName: string | null;
  msAgentName: string | null;
  /** Purchase total, kopeks. null when hidden from a SELLER. */
  msSum: number | null;
  status: ShipmentStatus;
  itemCount: number;
  pricedCount: number;
  /** POSTED: frozen total; PENDING: running sum of priced lines. */
  issueTotal: number | null;
  pairId: string | null;
  postedAt: string | null;
  msChangedAfterPost: boolean;
  msDeletedAt: string | null;
  /** Open MoySklad return draft / last sync failure; null when nothing was returned. */
  msReturn: MsReturnView | null;
  /** Who created a manual document (null: MoySklad-synced) / who last posted it. */
  createdByName: string | null;
  postedByName: string | null;
}

export interface ManualItemBody {
  name: string;
  quantityMilli: number;
  /** Optional per-line sklad; falls back to the document's header sklad. */
  skladId?: string;
  buyPriceKopeks?: number | null;
  issuePriceKopeks?: number | null;
}

export interface ManualShipmentBody {
  date: string;
  storeId: string;
  skladId: string;
  msName?: string;
  items: ManualItemBody[];
}

/** One owner-added line (goods from another sklad) on any накладная. */
export interface ExtraItemBody {
  skladId: string;
  name: string;
  quantityMilli: number;
  buyPriceKopeks?: number | null;
  issuePriceKopeks?: number | null;
}

/** Replaces the owner-added (MANUAL-origin) items; MoySklad items untouched. */
export interface ExtraItemsBody {
  items: ExtraItemBody[];
}

export type Shipment = ShipmentListItem & {
  items: ShipmentItem[];
  /** Only on the response of a movement action (return/undo/retry). */
  warnings?: ShipmentWarning[];
};

// ---------------------------------------------------------------- перемещения

export interface MovementRow {
  id: string;
  kind: MovementKind;
  createdAt: string;
  undoneAt: string | null;
  quantityMilli: number;
  item: {
    name: string;
    buyPriceKopeks: number | null;
    issuePriceKopeks: number | null;
    buySumKopeks: number | null;
    issueSumKopeks: number | null;
  };
  source: {
    shipmentId: string;
    msName: string;
    msDate: string;
    source: ShipmentSource;
    storeId: string;
    storeName: string;
  };
  dest: {
    storeId: string;
    storeName: string;
    shipmentId: string | null;
    msName: string | null;
  } | null;
  sklad: { id: string; name: string } | null;
  actor: { userId: string | null; userName: string | null } | null;
  undoneBy: { userName: string | null; at: string } | null;
  msReturn: { id: string; name: string | null; href: string | null } | null;
}

export interface MovementListResult {
  items: MovementRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListMovementsParams {
  kind?: MovementKind;
  from?: string;
  to?: string;
  storeId?: string;
  storeIds?: string[];
  /** Only rows whose SOURCE накладная belongs to this store (goods that left it). */
  sourceStoreId?: string;
  includeUndone?: boolean;
  page?: number;
  pageSize?: number;
}

/** One store-day of the «Перемещения» grid (Σ sklad price of goods that left). */
export interface MovementDayCell {
  buySumKopeks: number;
  count: number;
  /** Lines without a sklad (buy) price — they count but add 0. */
  unpricedCount: number;
}

export interface MovementsDayGridDay {
  date: string;
  byStore: Record<string, MovementDayCell>;
  total: MovementDayCell;
}

export interface MovementsDayGrid {
  month: string;
  kind: MovementKind | null;
  stores: { id: string; name: string }[];
  /** Only days with activity. */
  days: MovementsDayGridDay[];
  totals: Record<string, MovementDayCell>;
  grand: MovementDayCell;
}

export interface MovementsDayGridParams {
  month: string;
  kind?: MovementKind;
  storeId?: string;
  storeIds?: string[];
}

export interface ShipmentListResult {
  items: ShipmentListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListShipmentsParams {
  status?: ShipmentStatus;
  storeId?: string;
  storeIds?: string[];
  page?: number;
  pageSize?: number;
}

export interface SavePricesBody {
  items: { itemId: string; issuePriceKopeks: number }[];
}

/** Most-recent-known prices for one product at one store; null = never set. */
export interface LastPrice {
  buyPriceKopeks: number | null;
  issuePriceKopeks: number | null;
}
/** Keyed by normalized product name (see lib/productName.ts). */
export type LastPricesResult = Record<string, LastPrice>;

/** One sklad's posted PURCHASE+ISSUE pair from a накладная. */
export interface ShipmentLeg {
  purchase: Operation;
  /** null for a free-giveaway-only leg (every line given away → no sell side). */
  issue: Operation | null;
  skladId: string;
  skladDebt: number;
}

export interface PostShipmentResult {
  /** One leg per sklad the document drew from. */
  legs: ShipmentLeg[];
  storeBalance: number;
  shipment: Shipment;
}

/** A same-date накладная a transfer can land on. */
export interface DestinationCandidate {
  id: string;
  msName: string;
  status: ShipmentStatus;
  source: ShipmentSource;
}

export interface TransferItemBody {
  quantityMilli: number;
  toStoreId: string;
  targetShipmentId?: string;
  confirmCreate?: boolean;
}

/** Transfer either lands (returns the updated source) or asks to create B's doc. */
export type TransferItemResult = { needsNewShipment: true } | { shipment: Shipment };

export type RefreshShipmentsResult = Record<
  | 'created'
  | 'updated'
  | 'ignored_unmapped'
  | 'ignored_not_applicable'
  | 'deleted_pending'
  | 'flagged_posted'
  | 'noop'
  | 'error',
  number
>;

export interface MoyskladStatus {
  tokenConfigured: boolean;
  tokenValid: boolean | null;
  tokenError: string | null;
  webhookSecretConfigured: boolean;
  publicUrl: string | null;
  skladId: string | null;
  skladName: string | null;
  mappedStores: number;
}

export interface MsCounterparty {
  id: string;
  name: string;
  /** «Бизнес · Магазин» when a store of ANOTHER business holds it. */
  takenBy: string | null;
}

export interface WebhookStatus {
  canRegister: boolean;
  isRegistered: boolean;
  webhooks: { id: string; action: string; enabled: boolean; matchesExpected: boolean }[];
}

// ---------------------------------------------------------------- day-grid («Jadval»)

/** A dynamic money column: a sklad (store grid) or a store (sklad grid). */
export interface DayGridColumn {
  id: string;
  name: string;
  /** Optional accent hex («#rrggbb»); present for sklad columns that set one. */
  color?: string | null;
}

/** Which day-grid column a PENDING request's money lands in once approved. */
export type PendingColumn = 'cash' | 'terminal' | 'salary' | 'expenses';

/** Kopeks awaiting approval on one day, laid out like the grid's columns. */
export interface PendingByColumn {
  cash: number;
  terminal: number;
  salary: number;
  expenses: number;
}

/** A still-PENDING seller submission shown inside the jadval cell it will land
 * in. NOT money: never part of any approved column, of `totals`, or of
 * `balanceAfter`. Empty for a seller, who sees the sums but cannot approve. */
export interface StoreDayGridPending {
  id: string;
  type: OperationType;
  column: PendingColumn;
  amount: number;
  categoryId: string | null;
  categoryName: string | null;
  paymentMethod: PaymentMethod | null;
  deductFromStoreBalance: boolean;
  comment: string | null;
  createdByName: string;
  createdAt: string;
  /** Grouped within THIS response (one store-month), unlike the queue's. */
  duplicateGroupId: string | null;
  duplicateOperationId: string | null;
  duplicateConfirmedAt: string | null;
}

/**
 * One unposted (PENDING_PRICING) накладная in a store day-grid cell — one entry
 * per source sklad it draws from, so a multi-sklad document shows in several
 * columns. `skladId` '' = no resolvable sklad → the «Прочее» column.
 */
export interface StoreDayGridPendingShipment {
  id: string;
  msName: string;
  skladId: string;
  /** Running Σ of this sklad's PRICED lines (0 while nothing is priced). */
  issueTotal: number;
  itemCount: number;
  pricedCount: number;
}

export interface StoreDayGridDay {
  date: string;
  issuedBySklad: Record<string, number>;
  issuedOther: number;
  cash: number;
  terminal: number;
  /** Informational: terminal × terminalFeeBps / taxBps (0 when rate is 0). */
  terminalFee: number;
  tax: number;
  salary: number;
  expenses: number;
  /** Awaiting approval on this day — displayed beside the approved money,
   * never added to it and never to `balanceAfter`. */
  pending: PendingByColumn;
  pendingRequests: StoreDayGridPending[];
  /** Unposted накладные dated this day, per sklad ('' = «Прочее») — shown
   * beside the posted column, never added to it or to `balanceAfter`. */
  pendingIssuedBySklad: Record<string, number>;
  pendingShipments: StoreDayGridPendingShipment[];
  balanceAfter: number;
}

export interface StoreDayGrid {
  month: string;
  sklads: DayGridColumn[];
  hasOther: boolean;
  days: StoreDayGridDay[];
  totals: {
    issuedBySklad: Record<string, number>;
    issuedOther: number;
    cash: number;
    terminal: number;
    terminalFee: number;
    tax: number;
    salary: number;
    expenses: number;
  };
  /** Month totals of the PENDING money — deliberately outside `totals`.
   * `issuedBySklad` / `shipments`: the unposted накладные's side of it. */
  pendingTotals: PendingByColumn & {
    count: number;
    issuedBySklad: Record<string, number>;
    shipments: number;
  };
  salaryCategoryId: string | null;
  /** Store rates behind the fee/tax columns; 0 = column hidden (also for sellers). */
  terminalFeeBps: number;
  taxBps: number;
}

export interface SkladDayGridDay {
  date: string;
  purchasedByStore: Record<string, number>;
  purchasedOther: number;
  payment: number;
  debtAfter: number;
}

export interface SkladDayGrid {
  month: string;
  stores: DayGridColumn[];
  hasOther: boolean;
  days: SkladDayGridDay[];
  totals: { purchasedByStore: Record<string, number>; purchasedOther: number; payment: number };
}

/** One store's cell in the all-stores summary grid («Сводка»). */
export interface SummaryDayCell {
  cash: number;
  terminal: number;
  /** EVERY EXPENSE attributed to the store — salary included. */
  expenses: number;
}

export interface SummaryStoreTotals extends SummaryDayCell {
  /** Informational: month terminal gross × store.terminalFeeBps / taxBps. */
  fee: number;
  tax: number;
  /** cash + terminal − fee − tax − expenses. */
  net: number;
}

export interface StoresSummaryDayGrid {
  month: string;
  stores: { id: string; name: string; terminalFeeBps: number; taxBps: number }[];
  /** Sparse: a store appears in `byStore` only on days with activity. */
  days: { date: string; byStore: Record<string, SummaryDayCell> }[];
  totals: Record<string, SummaryStoreTotals>;
  grand: SummaryStoreTotals;
}

/** Who entered / approved a money row; nulls for legacy or system rows. */
export interface ActorNames {
  enteredByName: string | null;
  approvedByName: string | null;
}

export interface StoreDayIssueItem extends ActorNames {
  operationId: string;
  amount: number;
  skladId: string | null;
  skladName: string | null;
  shipmentId: string | null;
  shipmentName: string | null;
  /** Who posted the накладная behind this issue (null for a hand entry). */
  postedByName: string | null;
  comment: string | null;
}

export interface StoreDayPaymentItem extends ActorNames {
  operationId: string;
  amount: number;
  paymentMethod: PaymentMethod | null;
  comment: string | null;
}

export interface DayExpenseItem extends ActorNames {
  operationId: string;
  amount: number;
  categoryId: string | null;
  categoryName: string | null;
  comment: string | null;
}

export interface StoreDayDetail {
  date: string;
  issues: StoreDayIssueItem[];
  payments: StoreDayPaymentItem[];
  salary: DayExpenseItem[];
  expenses: DayExpenseItem[];
}

export interface SkladDayPurchaseItem extends ActorNames {
  operationId: string;
  amount: number;
  storeId: string | null;
  storeName: string | null;
  shipmentId: string | null;
  shipmentName: string | null;
  postedByName: string | null;
  comment: string | null;
}

export interface SkladDayDetail {
  date: string;
  purchases: SkladDayPurchaseItem[];
  payments: ({ operationId: string; amount: number; comment: string | null } & ActorNames)[];
}

// ---------------------------------------------------------------- dashboard

export interface DashboardView {
  totalStoreBalance: number;
  totalSkladDebt: number;
  month: { issued: number; paid: number; expenses: number; purchases: number };
  paidByMethod: PaidByMethod;
  stores: { id: string; name: string; balance: number }[];
}

// ---------------------------------------------------------------- product analytics

export interface ProductAnalyticsParams {
  /** Inclusive business dates, YYYY-MM-DD. */
  from: string;
  to: string;
  /** `posted` (default) = проведённые only; `all` also counts drafts. */
  status?: 'posted' | 'all';
}

export interface ProductAnalyticsRow {
  /** normalizeName(product name) — the grouping key and the drill-down id. */
  key: string;
  /** The spelling from the most recent накладная holding the product. */
  name: string;
  /** Σ active quantity (net of transfers away and returns), milli-units. */
  quantityMilli: number;
  /** The part of quantityMilli whose lines carry BOTH prices (profit basis). */
  pricedQuantityMilli: number;
  buySumKopeks: number;
  /** Buy sum of the lines that also carry an issue price — the наценка base. */
  pricedBuySumKopeks: number;
  issueSumKopeks: number;
  profitKopeks: number;
  avgBuyPriceKopeks: number | null;
  avgIssuePriceKopeks: number | null;
  /** Наценка = profit / buy sum, basis points; null when nothing was bought. */
  marginBps: number | null;
  /** Share in the period's total issue sum, basis points. */
  shareBps: number;
  docCount: number;
  storeCount: number;
  lastDate: string;
}

export interface ProductAnalyticsTotals {
  quantityMilli: number;
  pricedQuantityMilli: number;
  buySumKopeks: number;
  pricedBuySumKopeks: number;
  issueSumKopeks: number;
  profitKopeks: number;
  productCount: number;
}

export interface ProductAnalyticsReport {
  from: string;
  to: string;
  status: 'posted' | 'all';
  rows: ProductAnalyticsRow[];
  totals: ProductAnalyticsTotals;
}

export interface ProductShipmentLine {
  shipmentId: string;
  msName: string;
  msDate: string;
  status: ShipmentStatus;
  msDeletedAt: string | null;
  storeId: string;
  storeName: string;
  itemId: string;
  name: string;
  origin: ShipmentItemOrigin;
  /** Active quantity (what still counts for this store). */
  quantityMilli: number;
  /** The line as entered — differs from quantityMilli after a transfer/return. */
  originalQuantityMilli: number;
  buyPriceKopeks: number | null;
  issuePriceKopeks: number | null;
  buySumKopeks: number | null;
  issueSumKopeks: number | null;
}

export interface ProductStoreSplit {
  storeId: string;
  storeName: string;
  quantityMilli: number;
  issueSumKopeks: number;
  profitKopeks: number;
}

export interface ProductShipmentsReport {
  key: string;
  name: string;
  from: string;
  to: string;
  /** Newest business date first. */
  lines: ProductShipmentLine[];
  byStore: ProductStoreSplit[];
}
